import {all, takeLatest} from '@redux-saga/core/effects';
import {PayloadAction} from '@reduxjs/toolkit';
import {select} from 'redux-saga/effects';

import {
  OCRMOffer,
  Offer,
  OfferPreferences,
  OfferSource,
  OfferType,
  OffersResponse,
} from 'models/Offer';
import {OfferFiltersDestination} from 'models/OfferFilters';
import {ReservationDate} from 'models/Reservations';
import {SaveOfferPrefOpts} from 'services/api/offers/offers';
import {OffersActions, SelectedFilters} from 'store/offers/offers.reducer';
import {
  offersSelector,
  selectedFilterSelector,
  selectedOfferSelector,
  selectedStartDateSelector,
} from 'store/offers/offers.selectors';

import {Property} from 'store/properties/properties.interface';
import {trackEvent} from 'utils/trackEvent';

import {propertiesDataSelector} from '../properties/properties.selectors';
import {PaymentsActions} from 'store/payments/payments.reducer';

function trackGetOffersList(action: PayloadAction<OffersResponse>) {
  const {offers = []} = action.payload;
  const event = {event: 'GetOffersList', offers};
  trackEvent(event);
}

function trackDestinationChanged(
  action: PayloadAction<OfferFiltersDestination>
) {
  const destination = action.payload;
  const event = {
    event: 'UpdateOfferFilters',
    offerFilters: {
      type: 'destination',
      value: destination?.name ? [destination.name.toUpperCase()] : [],
    },
  };

  trackEvent(event);
}

function trackPropertyChanged(action: PayloadAction<Property[]>) {
  const properties = action.payload.length
    ? action.payload.map(p => p.name.toUpperCase())
    : ['ALL HOTELS AND RESORTS'];
  const event = {
    event: 'UpdateOfferFilters',
    offerFilters: {
      type: 'property',
      value: properties,
    },
  };

  trackEvent(event);
}

function* trackEndDateChanged(action: PayloadAction<string>) {
  const date = action?.payload;

  /**
   * A new start date was selected, date will be undefined.
   * We do not need to track as the start date event above will.
   */
  if (!date) {
    return;
  }

  const startDate = yield select(selectedStartDateSelector);
  const event = {
    event: 'UpdateOfferFilters',
    offerFilters: {
      type: 'dates',
      manualReservation: 'manual',
      date_Start: startDate,
      date_End: date,
    },
  };

  trackEvent(event);
}

function* trackReservationSelected(action: PayloadAction<ReservationDate>) {
  const startDate = yield select(selectedStartDateSelector);

  // Reservation was cleared, do not record the event.
  if (!startDate) {
    return;
  }

  const reservationDate = action.payload;

  const event = {
    event: 'UpdateOfferFilters',
    offerFilters: {
      type: 'dates',
      manualReservation: 'reservation dates',
      date_Start: reservationDate.checkInDate,
      date_End: reservationDate.checkOutDate,
    },
  };

  trackEvent(event);
}

function trackOfferTypeChanged(action: PayloadAction<OfferType[]>) {
  const types = action.payload;
  const event = {
    event: 'UpdateOfferFilters',
    offerFilters: {
      type: 'type',
      value: types.map(t => t.toUpperCase()) ?? [],
    },
  };

  trackEvent(event);
}

/**
 * This tracks filters that were applied all at the same time.
 * This is done when a user cancels out of the filter modal
 * or presses the clear filters from the modal.
 *
 * We will need to build the payloads for each filter applied and
 * send a separate tracking event for each one.
 *
 * Also handles cleared case (selected filters is reset)
 */
function* trackSelectedFiltersChanged(
  action: PayloadAction<SelectedFilters | undefined>
) {
  // Only track filter changes if we have offers loaded
  // This prevents tracking on initial page load when setting default destination
  const offers = yield select(offersSelector);
  const hasOffers = Object.keys(offers).length > 0;

  if (!hasOffers) {
    return;
  }

  const filters = action.payload;

  const destinationFilterEvent = {
    event: 'UpdateOfferFilters',
    offerFilters: {
      type: 'destination',
      value: filters?.destination?.name
        ? [filters.destination.name.toUpperCase()]
        : [],
    },
  };

  const propertiesFilterEvent = {
    event: 'UpdateOfferFilters',
    offerFilters: {
      type: 'property',
      value: filters?.properties?.map(p => p.name.toUpperCase()) ?? [],
    },
  };

  const dateFilterEvent = {
    event: 'UpdateOfferFilters',
    offerFilters: {
      type: 'dates',
      manualReservation: 'manual',
      date_Start: filters?.startDate?.toUpperCase() ?? '',
      date_End: filters?.endDate?.toUpperCase() ?? '',
    },
  };

  const offerTypeEvent = {
    event: 'UpdateOfferFilters',
    offerFilters: {
      type: 'type',
      value: filters?.type?.map(t => t.toUpperCase()) ?? [],
    },
  };

  trackEvent(destinationFilterEvent);
  trackEvent(propertiesFilterEvent);
  trackEvent(dateFilterEvent);
  trackEvent(offerTypeEvent);
}

function* trackFavoriteOffer(action: PayloadAction<SaveOfferPrefOpts>) {
  const {offerid, preference} = action.payload;

  // we don't want to track unfavorite action
  if (preference !== OfferPreferences.Favorite) {
    return;
  }
  const offers = yield select(offersSelector);
  const offer = offers[offerid];
  const isOcrm = offer.source === OfferSource.ocrm;

  const getOcrmOfferType = (ocrmOffer: OCRMOffer) => {
    const offerTypes = ocrmOffer.ocrm.rewards.map(reward => reward.refinedType);
    const hasSameOfferTypes = offerTypes.every(type => type === offerTypes[0]);

    if (hasSameOfferTypes) {
      return offerTypes[0];
    }

    return 'multiple';
  };

  const favoriteOfferEvent = {
    event: 'favoriteOffer',
    offerDetails: {
      offerCode: offer.id,
      offerType: isOcrm ? getOcrmOfferType(offer) : offer.refinedType,
      propertyCode: offer.propertyList.join(','),
    },
  };

  trackEvent(favoriteOfferEvent);
}

function* trackOfferDetails() {
  const offer: Offer | undefined = yield select(selectedOfferSelector);
  const {destination, properties, startDate, endDate, type}: SelectedFilters =
    yield select(selectedFilterSelector);
  if (!offer) {
    return;
  }
  const event = {
    event: 'ViewOfferDetails',
    offerDetails: {
      isFavorite: offer.pref === OfferPreferences.Favorite,
      offerCode: offer.id,
    },
    filterDetails: {
      destination: destination?.name,
      hotels:
        destination && properties?.length === 0
          ? 'ALL HOTELS AND RESORTS'
          : properties?.map(p => p.name).join('|'),
      date_Start: startDate,
      date_End: endDate,
      type: type?.join('|'),
    },
  };

  trackEvent(event);
}

function* trackViewOfferCalendar(action: PayloadAction<string | undefined>) {
  const offer: Offer | undefined = yield select(selectedOfferSelector);
  const properties = yield select(propertiesDataSelector);
  const selectedProperty = properties.filter(
    property => property.id === action.payload
  );
  if (!offer) {
    return;
  }
  const event = {
    event: 'ViewOfferCalendarButton',
    offerDetails: {
      propertySelected: selectedProperty[0].name,
      offerCode: offer.id,
      availableProperties: offer.propertyList,
    },
  };

  trackEvent(event);
}

function* trackAcceptOffer() {
  const offer: Offer | undefined = yield select(selectedOfferSelector);
  if (!offer) {
    return;
  }
  const getOcrmOfferType = OCRMOfferParam => {
    if (OCRMOfferParam.source !== OfferSource.ocrm) {
      return OCRMOfferParam?.refinedType;
    }
    const offerTypes = OCRMOfferParam.ocrm.rewards.map(
      reward => reward.refinedType
    );
    const hasSameOfferTypes = offerTypes.every(type => type === offerTypes[0]);

    if (hasSameOfferTypes) {
      return offerTypes[0];
    }

    return 'multiple';
  };
  const event = {
    event: 'AcceptOfferSuccess',
    offerDetails: {
      isFavorite: offer.pref === OfferPreferences.Favorite,
      offerCode: offer.id,
      offerType: getOcrmOfferType(offer),
      propertyCode: offer.propertyList.join(','),
    },
  };

  trackEvent(event);
}

function trackPaymentUpdate() {
  const event = {
    event: 'payment_method_update',
    addremove: 'add',
  };

  trackEvent(event);
}

function trackPaymentRemove() {
  const event = {
    event: 'payment_method_update',
    addremove: 'remove',
  };

  trackEvent(event);
}

export default function* analyticsSagas() {
  yield all([
    takeLatest(OffersActions.getOffersSuccess, trackGetOffersList),
    takeLatest(
      OffersActions.setSelectedDestinationFilter.type,
      trackDestinationChanged
    ),
    takeLatest(
      OffersActions.setSelectedPropertyFilter.type,
      trackPropertyChanged
    ),
    takeLatest(OffersActions.setEndDate.type, trackEndDateChanged),
    takeLatest(OffersActions.reservationSelected, trackReservationSelected),
    takeLatest(OffersActions.setSelectedOfferTypeFilter, trackOfferTypeChanged),
    takeLatest(OffersActions.setSelectedFilters, trackSelectedFiltersChanged),
    takeLatest(OffersActions.saveOfferPref, trackFavoriteOffer),
    takeLatest(OffersActions.setSelectedOfferId, trackOfferDetails),
    takeLatest(OffersActions.bookOfferCalendar, trackViewOfferCalendar),
    takeLatest(OffersActions.acceptOfferSuccess, trackAcceptOffer),
    takeLatest(PaymentsActions.saveCard, trackPaymentUpdate),
    takeLatest(PaymentsActions.deleteCard, trackPaymentRemove),
  ]);
}
